import { ref } from 'vue'
import type { Article } from './articles'

export const useBulkEdit = (getArticles: () => any) => {
  const selectedIds = ref<string[]>([])

  // ===== BULK OPERATIONS =====
  const bulkFreezeArticles = async (restaurantId: string, ids: string[], freeze: boolean, apiInstance: any): Promise<boolean> => {
    try {
      const action = freeze ? 'freeze' : 'unfreeze'
      const itemIdSet = new Set(ids)

      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/menu-items/bulk-freeze`,
        {
          items: ids,
          action: action
        }
      )

      // Update local articles state
      const articles = getArticles()
      const updateFrozenState = (category: any) => {
        category.food_items?.map((item: any) => {
          if (itemIdSet.has(item.id.toString())) {
            item.is_frozen = freeze ? 1 : 0
          }
        });
        [category.sub_categories, category.sub_sub_categories].map(subCategories =>
          subCategories?.map((subCat: any) => updateFrozenState(subCat))
        )
      }
      articles.map(updateFrozenState)

      return true
    } catch (error: any) {
      console.error('❌ Error freezing/unfreezing items:', error)
      throw error
    }
  }

  const bulkDigitalMenuArticles = async (restaurantId: string, ids: string[], activate: boolean, apiInstance: any): Promise<boolean> => {
    try {
      const itemIdSet = new Set(ids)

      await apiInstance.post(`/restaurant/${restaurantId}/menu-items/digital-menu-status`,
        {
          items: ids,
          digital_menu_activate: activate ? 1 : 0
        }
      )

      const articles = getArticles()
      const updateDigitalMenuState = (category: any) => {
        category.food_items?.map((item: any) => {
          if (itemIdSet.has(item.id.toString())) {
            item.is_digital_menu = activate ? 1 : 0
          }
        });
        [category.sub_categories, category.sub_sub_categories].map(subCategories =>
          subCategories?.map((subCat: any) => updateDigitalMenuState(subCat))
        )
      }
      articles.map(updateDigitalMenuState)

      return true
    } catch (error: any) {
      console.error('❌ Error bulk toggling digital menu:', error)
      throw error
    }
  }

  const bulkUpdateArticles = async (restaurantId: string, payload: any, apiInstance: any): Promise<boolean> => {
    try {
      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/menu-items/bulk-update`,
        payload
      )

      if (response.status === 200) {
        if (payload.order_modes !== undefined && payload.item_ids) {
          const itemIdSet = new Set(payload.item_ids.map(String))
          const articles = getArticles()
          const updateOrderModes = (category: any) => {
            category.food_items?.forEach((item: any) => {
              if (itemIdSet.has(item.id.toString())) {
                item.order_modes = payload.erase_existing_modes ? [...payload.order_modes] : [...new Set([...(item.order_modes || []), ...payload.order_modes])]
              }
            });
            [category.sub_categories, category.sub_sub_categories].forEach((subCategories: any) =>
              subCategories?.forEach((subCat: any) => updateOrderModes(subCat))
            )
          }
          articles.forEach(updateOrderModes)
        }
        return true
      } else {
        const errorMessage = response.data?.message || 'Failed to update items'
        console.error('❌ Error updating items:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update items'
      console.error('❌ Error updating items:', errorMessage)
      const wrapped: any = new Error(errorMessage)
      // Preserve backend field-level validation errors so the form can map them to fields
      wrapped.fieldErrors = error?.response?.data?.errors || null
      throw wrapped
    }
  }

  // ===== SELECTION MANAGEMENT =====
  const toggleSelection = (id: string) => {
    const index = selectedIds.value.indexOf(id)
    if (index > -1) {
      selectedIds.value.splice(index, 1)
    } else {
      selectedIds.value.push(id)
    }
  }

  const selectAll = (restaurantId: string) => {
    // Placeholder for future implementation
  }

  const deselectAll = () => {
    selectedIds.value = []
  }

  const isSelected = (id: string) => {
    return selectedIds.value.includes(id)
  }

  const getSelectedCount = () => {
    return selectedIds.value.length
  }

  const getSelected = () => {
    return []
  }

  return {
    selectedIds,
    bulkFreezeArticles,
    bulkDigitalMenuArticles,
    bulkUpdateArticles,
    toggleSelection,
    selectAll,
    deselectAll,
    isSelected,
    getSelectedCount,
    getSelected,
  }
}
